declare const React: any;

import { loadJson, saveJson } from "../game/storage.js";
import { buildCountyOptions, countyAnswerMatches, countyAutocomplete, selectCountyRound } from "./core.js";
import type { CountyShape } from "./core.js";

export type CountyEra = "current" | "pre2018";
type CountyAnswerMethod = "text" | "choices-3" | "choices-5";
type CountyRoundLength = 5 | 10 | "all";
type CountyPhase = "setup" | "game" | "results";

interface CountyMapData {
  era: CountyEra;
  label: string;
  viewBox: string;
  source: string;
  license: string;
  counties: CountyShape[];
}

interface CountySettings {
  era: CountyEra;
  answerMethod: CountyAnswerMethod;
  roundLength: CountyRoundLength;
}

interface CountyAnswerRecord {
  countyId: string;
  countyName: string;
  userAnswer: string;
  correct: boolean;
}

interface Props {
  onBack: () => void;
}

interface State {
  loading: boolean;
  error: string;
  maps: Partial<Record<CountyEra, CountyMapData>>;
  phase: CountyPhase;
  settings: CountySettings;
  questions: CountyShape[];
  questionIndex: number;
  answerInput: string;
  suggestions: string[];
  suggestionIndex: number;
  feedback: CountyAnswerRecord | null;
  records: CountyAnswerRecord[];
  correct: number;
  streak: number;
  bestStreak: number;
  startedAt: number;
  finishedAt: number;
}

const DEFAULT_COUNTY_SETTINGS: CountySettings = {
  era: "current",
  answerMethod: "choices-3",
  roundLength: 10,
};

function NorwayCountyMap(props: { data: CountyMapData; targetId?: string; feedback?: "correct" | "wrong" | null; label?: string }) {
  const { data, targetId = "", feedback = null, label = "Norgeskart med fylkesgrenser" } = props;
  return (
    <svg className={`norway-county-map ${feedback ? `answered-${feedback}` : ""}`} viewBox={data.viewBox} role="img" aria-label={label}>
      {data.counties.map((county) => (
        <g
          key={county.id}
          className={`norway-county-shape ${county.id === targetId ? "target" : ""}`}
          data-county={county.id}
          dangerouslySetInnerHTML={{ __html: county.markup }}
        />
      ))}
    </svg>
  );
}

function SettingsChoice(props: { selected: boolean; title: string; subtitle: string; onClick: () => void; badge?: string }) {
  return (
    <button type="button" className={`county-setting-choice ${props.selected ? "selected" : ""}`} onClick={props.onClick}>
      <span><strong>{props.title}</strong><small>{props.subtitle}</small></span>
      {props.badge && <b>{props.badge}</b>}
      <i aria-hidden="true">{props.selected ? "✓" : ""}</i>
    </button>
  );
}

export class NorwayCountiesGame extends React.Component<Props, State> {
  state: State = {
    loading: true,
    error: "",
    maps: {},
    phase: "setup",
    settings: DEFAULT_COUNTY_SETTINGS,
    questions: [],
    questionIndex: 0,
    answerInput: "",
    suggestions: [],
    suggestionIndex: -1,
    feedback: null,
    records: [],
    correct: 0,
    streak: 0,
    bestStreak: 0,
    startedAt: 0,
    finishedAt: 0,
  };

  componentDidMount() {
    const settings = loadJson<CountySettings>(localStorage, "geospillet.counties.settings.v1", DEFAULT_COUNTY_SETTINGS);
    window.addEventListener("keydown", this.handleGlobalKeys);
    Promise.all([
      fetch("/data/norway-counties-current.json").then((response) => {
        if (!response.ok) throw new Error(`Kunne ikke laste dagens fylkeskart (${response.status}).`);
        return response.json();
      }),
      fetch("/data/norway-counties-pre2018.json").then((response) => {
        if (!response.ok) throw new Error(`Kunne ikke laste historisk fylkeskart (${response.status}).`);
        return response.json();
      }),
    ])
      .then(([current, pre2018]) => this.setState({ maps: { current, pre2018 }, settings: { ...DEFAULT_COUNTY_SETTINGS, ...settings }, loading: false }))
      .catch((error) => this.setState({ error: String(error.message || error), loading: false }));
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleGlobalKeys);
  }

  currentMap = (): CountyMapData | undefined => this.state.maps[this.state.settings.era];
  currentCounty = (): CountyShape | undefined => this.state.questions[this.state.questionIndex];

  updateSettings = (patch: Partial<CountySettings>) => {
    const settings = { ...this.state.settings, ...patch };
    saveJson(localStorage, "geospillet.counties.settings.v1", settings);
    this.setState({ settings, answerInput: "", suggestions: [], suggestionIndex: -1 });
  };

  startRound = () => {
    const map = this.currentMap();
    if (!map) return;
    const count = this.state.settings.roundLength === "all" ? map.counties.length : Math.min(this.state.settings.roundLength, map.counties.length);
    this.setState({
      phase: "game",
      questions: selectCountyRound(map.counties, count),
      questionIndex: 0,
      answerInput: "",
      suggestions: [],
      suggestionIndex: -1,
      feedback: null,
      records: [],
      correct: 0,
      streak: 0,
      bestStreak: 0,
      startedAt: Date.now(),
      finishedAt: 0,
    }, () => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  submitAnswer = (rawAnswer: string) => {
    const county = this.currentCounty();
    if (!county || this.state.feedback || !rawAnswer.trim()) return;
    const correct = countyAnswerMatches(rawAnswer, county.name);
    const record: CountyAnswerRecord = { countyId: county.id, countyName: county.name, userAnswer: rawAnswer, correct };
    const streak = correct ? this.state.streak + 1 : 0;
    this.setState({
      feedback: record,
      records: [...this.state.records, record],
      correct: this.state.correct + (correct ? 1 : 0),
      streak,
      bestStreak: Math.max(this.state.bestStreak, streak),
      answerInput: rawAnswer,
      suggestions: [],
      suggestionIndex: -1,
    });
  };

  nextQuestion = () => {
    if (this.state.questionIndex >= this.state.questions.length - 1) {
      this.setState({ phase: "results", finishedAt: Date.now(), feedback: null }, () => window.scrollTo({ top: 0, behavior: "smooth" }));
      return;
    }
    this.setState({ questionIndex: this.state.questionIndex + 1, answerInput: "", suggestions: [], suggestionIndex: -1, feedback: null });
  };

  handleGlobalKeys = (event: KeyboardEvent) => {
    if (this.state.phase !== "game") return;
    if (this.state.feedback && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      this.nextQuestion();
      return;
    }
    if (this.state.feedback || this.state.settings.answerMethod === "text") return;
    const county = this.currentCounty();
    const map = this.currentMap();
    if (!county || !map) return;
    const count = this.state.settings.answerMethod === "choices-5" ? 5 : 3;
    const options = this.optionsForCurrent(count);
    const index = Number(event.key) - 1;
    if (index >= 0 && index < options.length) {
      event.preventDefault();
      this.submitAnswer(options[index]);
    }
  };

  optionsCache: { key: string; values: string[] } | null = null;
  optionsForCurrent = (count: 3 | 5): string[] => {
    const county = this.currentCounty();
    const map = this.currentMap();
    if (!county || !map) return [];
    const key = `${this.state.settings.era}-${county.id}-${count}`;
    if (this.optionsCache?.key === key) return this.optionsCache.values;
    const values = buildCountyOptions(county, map.counties, count);
    this.optionsCache = { key, values };
    return values;
  };

  handleTextInput = (value: string) => {
    const map = this.currentMap();
    this.setState({
      answerInput: value,
      suggestions: map ? countyAutocomplete(map.counties, value) : [],
      suggestionIndex: -1,
    });
  };

  handleTextKey = (event: any) => {
    const { suggestions, suggestionIndex } = this.state;
    if (event.key === "ArrowDown" && suggestions.length) {
      event.preventDefault();
      this.setState({ suggestionIndex: Math.min(suggestionIndex + 1, suggestions.length - 1) });
    } else if (event.key === "ArrowUp" && suggestions.length) {
      event.preventDefault();
      this.setState({ suggestionIndex: Math.max(suggestionIndex - 1, 0) });
    } else if (event.key === "Escape") {
      this.setState({ suggestions: [], suggestionIndex: -1 });
    } else if (event.key === "Enter" && suggestionIndex >= 0 && suggestions[suggestionIndex]) {
      event.preventDefault();
      const choice = suggestions[suggestionIndex];
      this.setState({ answerInput: choice, suggestions: [], suggestionIndex: -1 }, () => this.submitAnswer(choice));
    }
  };

  renderSetup() {
    const current = this.state.maps.current;
    const pre2018 = this.state.maps.pre2018;
    const map = this.currentMap();
    if (!map || !current || !pre2018) return null;
    return (
      <main className="county-page county-setup-page">
        <div className="county-subnav">
          <button className="ghost" onClick={this.props.onBack}>← Til verdensspillet</button>
          <span>🇳🇴 Norges fylker</span>
        </div>
        <section className="county-hero-card">
          <div className="county-hero-copy">
            <span className="eyebrow">Egen spillmodus</span>
            <h1>Norges <em>fylker</em></h1>
            <p>Et fylke lyser opp på Norgeskartet. Skriv navnet selv, eller velg mellom 3 eller 5 alternativer.</p>
            <div className="county-pills"><span>15 fylker i dag</span><span>19 fylker i 2017</span><span>Lokale kartdata</span></div>
          </div>
          <div className="county-preview-map"><NorwayCountyMap data={map} /></div>
        </section>

        <section className="county-config card">
          <div className="config-heading"><div><span className="step-number">1</span><div><h2>Velg fylkesinndeling</h2><p>Dagens inndeling er standard.</p></div></div></div>
          <div className="county-era-grid">
            <SettingsChoice selected={this.state.settings.era === "current"} title="Dagens inndeling" subtitle="Gjeldende fylker fra 2024" badge={`${current.counties.length} fylker`} onClick={() => this.updateSettings({ era: "current" })} />
            <SettingsChoice selected={this.state.settings.era === "pre2018"} title="Før regionreformen" subtitle="Fylkesinndelingen i 2017" badge={`${pre2018.counties.length} fylker`} onClick={() => this.updateSettings({ era: "pre2018" })} />
          </div>
          <p className="county-history-note">Historisk modus bruker 2017-kartet, altså før Sør-Trøndelag og Nord-Trøndelag ble slått sammen til Trøndelag 1. januar 2018.</p>

          <div className="config-divider" />
          <div className="county-options-row">
            <fieldset className="setting-card">
              <legend>Svarmetode</legend>
              {[
                { id: "text", label: "Skriv selv", sub: "Med autofullføring" },
                { id: "choices-3", label: "3 alternativer", sub: "Raskt og oversiktlig" },
                { id: "choices-5", label: "5 alternativer", sub: "Mer krevende" },
              ].map((item) => (
                <label key={item.id} className={this.state.settings.answerMethod === item.id ? "choice-row selected" : "choice-row"}>
                  <input type="radio" name="county-answer-method" checked={this.state.settings.answerMethod === item.id} onChange={() => this.updateSettings({ answerMethod: item.id as CountyAnswerMethod })} />
                  <span><strong>{item.label}</strong><small>{item.sub}</small></span><i />
                </label>
              ))}
            </fieldset>
            <fieldset className="setting-card">
              <legend>Antall oppgaver</legend>
              <div className="length-buttons county-length-buttons">
                {[5, 10].map((length) => <button type="button" key={length} className={this.state.settings.roundLength === length ? "selected" : ""} onClick={() => this.updateSettings({ roundLength: length as 5 | 10 })}>{length}</button>)}
                <button type="button" className={this.state.settings.roundLength === "all" ? "selected" : ""} onClick={() => this.updateSettings({ roundLength: "all" })}>Alle</button>
              </div>
              <small>{this.state.settings.roundLength === "all" ? map.counties.length : Math.min(this.state.settings.roundLength, map.counties.length)} fylker kommer i runden. Ingen gjentakelser.</small>
              <button className="primary county-start-button" onClick={this.startRound}>Start fylkesspillet <span>→</span></button>
            </fieldset>
          </div>
        </section>
      </main>
    );
  }

  renderAnswerArea(county: CountyShape, map: CountyMapData) {
    if (this.state.settings.answerMethod === "text") {
      return (
        <form className="county-text-answer" onSubmit={(event: any) => { event.preventDefault(); this.submitAnswer(this.state.answerInput); }}>
          <div className="autocomplete-wrap">
            <label htmlFor="county-answer">Skriv fylkesnavnet</label>
            <input id="county-answer" autoComplete="off" value={this.state.answerInput} onChange={(event: any) => this.handleTextInput(event.target.value)} onKeyDown={this.handleTextKey} aria-autocomplete="list" aria-expanded={this.state.suggestions.length > 0} placeholder="For eksempel Akershus" autoFocus />
            {this.state.suggestions.length > 0 && <div className="suggestions" role="listbox">{this.state.suggestions.map((suggestion, index) => <button type="button" key={suggestion} className={this.state.suggestionIndex === index ? "active" : ""} onMouseDown={(event: any) => event.preventDefault()} onClick={() => this.setState({ answerInput: suggestion, suggestions: [], suggestionIndex: -1 }, () => this.submitAnswer(suggestion))}>{suggestion}</button>)}</div>}
          </div>
          <button className="primary" type="submit" disabled={!this.state.answerInput.trim()}>Svar</button>
        </form>
      );
    }
    const count = this.state.settings.answerMethod === "choices-5" ? 5 : 3;
    const options = this.optionsForCurrent(count);
    return (
      <div className={`county-choice-grid county-choices-${count}`}>
        {options.map((option, index) => <button key={option} onClick={() => this.submitAnswer(option)}><kbd>{index + 1}</kbd><strong>{option}</strong><i>→</i></button>)}
      </div>
    );
  }

  renderGame() {
    const map = this.currentMap();
    const county = this.currentCounty();
    if (!map || !county) return null;
    const progress = ((this.state.questionIndex + (this.state.feedback ? 1 : 0)) / this.state.questions.length) * 100;
    const feedbackState = this.state.feedback ? (this.state.feedback.correct ? "correct" : "wrong") : null;
    return (
      <main className="county-page county-game-page">
        <div className="county-game-topbar">
          <button className="ghost" onClick={() => this.setState({ phase: "setup", feedback: null })}>← Avslutt</button>
          <div><strong>Fylke {this.state.questionIndex + 1} av {this.state.questions.length}</strong><small>{map.label}</small></div>
          <div className="county-game-score"><span><b>{this.state.correct}</b> riktige</span><span><b>{this.state.streak}</b> på rad</span></div>
        </div>
        <div className="county-progress"><i style={{ width: `${progress}%` }} /></div>

        <section className={`county-game-shell ${feedbackState ? `is-${feedbackState}` : ""}`}>
          <div className="county-map-panel">
            <NorwayCountyMap data={map} targetId={county.id} feedback={feedbackState} label={this.state.feedback ? `${county.name} markert på Norgeskartet` : "Norgeskart med ett fylke markert. Finn fylket."} />
          </div>
          <div className="county-answer-panel">
            {!this.state.feedback ? <div className="county-question-content">
              <span className="eyebrow">Finn fylket</span>
              <h1>Hvilket fylke er markert?</h1>
              <p>Det markerte området er fasiten.</p>
              {this.renderAnswerArea(county, map)}
            </div> : <div className={`county-feedback ${this.state.feedback.correct ? "correct" : "wrong"}`}>
              <div className="county-feedback-symbol">{this.state.feedback.correct ? "✓" : "×"}</div>
              <div><span>{this.state.feedback.correct ? "Riktig!" : "Riktig svar er"}</span><h2>{county.name}</h2>{!this.state.feedback.correct && <p>Du svarte {this.state.feedback.userAnswer}.</p>}</div>
              <button className="primary" onClick={this.nextQuestion}>{this.state.questionIndex >= this.state.questions.length - 1 ? "Se resultat" : "Neste"} →</button>
            </div>}
          </div>
        </section>
      </main>
    );
  }

  renderResults() {
    const map = this.currentMap();
    if (!map) return null;
    const total = this.state.records.length;
    const percent = total ? Math.round(this.state.correct / total * 100) : 0;
    const seconds = Math.max(0, Math.round(((this.state.finishedAt || Date.now()) - this.state.startedAt) / 1000));
    const wrong = this.state.records.filter((record) => !record.correct);
    return (
      <main className="county-page county-results-page">
        <div className="county-subnav"><button className="ghost" onClick={this.props.onBack}>← Til verdensspillet</button><span>🇳🇴 Norges fylker</span></div>
        <section className="county-results-card card">
          <span className="eyebrow">Runden er ferdig</span>
          <h1>{percent >= 80 ? "Sterkt fylkeskjent!" : percent >= 50 ? "Godt jobbet!" : "Ny runde?"}</h1>
          <p>{this.state.correct} av {total} riktige med {map.label.toLowerCase()}.</p>
          <div className="county-result-metrics">
            <div><strong>{percent}%</strong><span>treff</span></div>
            <div><strong>{this.state.correct}/{total}</strong><span>riktige</span></div>
            <div><strong>{this.state.bestStreak}</strong><span>beste rekke</span></div>
            <div><strong>{seconds}s</strong><span>tid</span></div>
          </div>
          {wrong.length > 0 && <div className="county-wrong-list"><span>Disse kan du ta én gang til</span><div>{wrong.map((record) => <b key={`${record.countyId}-${record.userAnswer}`}>{record.countyName}</b>)}</div></div>}
          <div className="county-result-actions">
            <button className="primary" onClick={this.startRound}>Spill samme oppsett igjen</button>
            <button className="secondary" onClick={() => this.setState({ phase: "setup", feedback: null })}>Endre oppsett</button>
            <button className="ghost" onClick={this.props.onBack}>Til verdensspillet</button>
          </div>
        </section>
      </main>
    );
  }

  render() {
    if (this.state.loading) return <main className="county-page county-loading"><strong>Laster fylkeskart …</strong><span /></main>;
    if (this.state.error) return <main className="county-page county-loading"><strong>Kunne ikke laste fylkesspillet</strong><p>{this.state.error}</p><button className="secondary" onClick={this.props.onBack}>Tilbake</button></main>;
    if (this.state.phase === "game") return this.renderGame();
    if (this.state.phase === "results") return this.renderResults();
    return this.renderSetup();
  }
}
